import { getAnalytics, logEvent } from 'firebase/analytics';
import { useSyncExternalStore } from 'react';
import { useStore } from 'react-redux';
import { saveShowSubscribeStatus, setSpeakerSubscribeStatus } from '../redux/actions/userActions';
import { getSubscribeStatus } from '../utils/subscribeStatus';
import { showSearchAliases } from '../utils/showSearch';

const sessions = new WeakMap();
const accountKey = user => user.authenticated && user.uid ? `${user.uid}:${user.forMeSessionRevision || 0}` : null;
const aliasesFor = (kind, entity) => kind === 'speaker' ? [`speaker:${entity.id}`] : showSearchAliases(entity);

export const entitySnapshot = (kind, entity, state, user) => {
    const aliases = aliasesFor(kind, entity);
    const aliasSave = aliases.map(key => state.savedShows[key]).find(Boolean);
    const saved = aliasSave && (state.savedShows[`source:${aliasSave.source.id}`] || aliasSave);
    const source = saved?.source || entity.source;
    const matchingMutations = [...aliases, ...(source?.id ? [`source:${source.id}`] : [])]
        .map(key => state.mutations[key]).filter(Boolean);
    const mutation = matchingMutations.find(value => value.saving) || matchingMutations[0];
    const id = kind === 'speaker' ? entity.id : source?.id;
    const statusById = kind === 'speaker' ? user.speakerSubscribeStatusById : user.podcastSubscribeStatusById;
    const subscribedIds = kind === 'speaker' ? user.speakerSubscribeIds : user.podcastSubscribeIds;
    const editedKey = `${kind === 'speaker' ? 'speaker' : 'source'}:${id}`;
    const hasEdit = Object.prototype.hasOwnProperty.call(user.entityPreferenceEdits || {}, editedKey);
    const knownStatus = Object.prototype.hasOwnProperty.call(statusById || {}, id) || subscribedIds?.includes(id);
    const storedStatus = getSubscribeStatus(statusById, subscribedIds, id);
    const status = !user.authenticated ? null : mutation ? (mutation.saving ? mutation.status : mutation.previousStatus) :
        hasEdit ? user.entityPreferenceEdits[editedKey].status : knownStatus ? storedStatus : saved ? saved.subscribeStatus : entity.subscribeStatus ?? null;
    return { status, mutation, source };
};

// The store owns the intent, so a streamed chat replacement or an account refresh
// cannot unmount the sign-in sheet or silently discard a failed save.
export const createEntityPreferenceSession = store => {
    let intentSequence = 0;
    let state = { intent: null, mutations: {}, savedShows: {}, error: null };
    const listeners = new Set();
    let account = accountKey(store.getState().user);
    const update = patch => {
        state = { ...state, ...patch };
        listeners.forEach(listener => listener());
    };
    const resetAccount = () => {
        const nextAccount = accountKey(store.getState().user);
        if (account === nextAccount) {
            const edits = store.getState().user.entityPreferenceEdits || {};
            const superseded = value => value && (edits[value.editKey]?.revision || 0) > value.editRevision;
            const mutations = Object.fromEntries(Object.entries(state.mutations)
                .filter(([, value]) => !value?.error || !superseded(value)));
            const error = superseded(state.error) ? null : state.error;
            if (Object.keys(mutations).length !== Object.keys(state.mutations).length || error !== state.error) update({ mutations, error });
            return;
        }
        const contextualSignIn = !account && nextAccount && state.intent?.phase === 'signin';
        account = nextAccount;
        update({ mutations: {}, savedShows: {}, error: null,
            intent: contextualSignIn ? { ...state.intent, account: nextAccount } : null });
    };
    store.subscribe(resetAccount);
    const save = async (kind, entity, status, previousStatus) => {
        resetAccount();
        const owner = account;
        if (!owner) return;
        const aliases = aliasesFor(kind, entity);
        if (!aliases.length || aliases.some(key => state.mutations[key]?.saving)) return;
        const failed = aliases.map(key => state.mutations[key]).find(value => value?.error);
        previousStatus = failed ? failed.previousStatus : previousStatus === undefined
            ? entitySnapshot(kind, entity, state, store.getState().user).status : previousStatus;
        const sourceId = entitySnapshot(kind, entity, state, store.getState().user).source?.id;
        const editKey = kind === 'speaker' ? `speaker:${entity.id}` : `source:${sourceId}`;
        const editRevision = store.getState().user.entityPreferenceEdits?.[editKey]?.revision || 0;
        const mutation = { saving: true, status, previousStatus, editKey, editRevision };
        logEvent(getAnalytics(), status === true ? 'subscribe_true' : status === false ? 'subscribe_false' : 'subscribe_null');
        update({ mutations: { ...state.mutations, ...Object.fromEntries(aliases.map(key => [key, mutation])) }, error: null });
        try {
            const result = await store.dispatch(kind === 'speaker'
                ? setSpeakerSubscribeStatus(entity.id, status, { suppressErrorAlert: true })
                : saveShowSubscribeStatus(entity, status));
            resetAccount();
            if (account !== owner) return;
            if (!result) throw new Error('Unable to save preference.');
            const savedShows = kind === 'show' ? { ...state.savedShows,
                ...Object.fromEntries([...aliases, `source:${result.source.id}`].map(key => [key, result])) } : state.savedShows;
            update({ savedShows, mutations: { ...state.mutations, ...Object.fromEntries(aliases.map(key => [key, null])) } });
        } catch {
            resetAccount();
            if (account !== owner) return;
            if ((store.getState().user.entityPreferenceEdits?.[editKey]?.revision || 0) > editRevision) {
                update({ mutations: { ...state.mutations, ...Object.fromEntries(aliases.map(key => [key, null])) } });
                return;
            }
            update({ mutations: { ...state.mutations, ...Object.fromEntries(aliases.map(key => [key, { ...mutation, saving: false, error: true }])) },
                error: { kind, entity, status, previousStatus, editKey, editRevision, account: owner } });
        }
    };
    return {
        subscribe: listener => { listeners.add(listener); return () => listeners.delete(listener); },
        getSnapshot: () => state,
        request: (kind, entity, status, previousStatus) => {
            resetAccount();
            if (!account) update({ intent: { id: ++intentSequence, kind, entity, status, phase: 'signin' } });
            else return save(kind, entity, status, previousStatus);
        },
        cancel: () => update({ intent: null }),
        signedIn: id => {
            if (state.intent?.id !== id) return;
            resetAccount();
            if (account && state.intent?.account === account) update({ intent: { ...state.intent, phase: 'closing' } });
            else update({ intent: null });
        },
        exited: id => {
            const intent = state.intent;
            if (intent?.id !== id) return;
            if (intent?.phase !== 'closing') return;
            update({ intent: null });
            resetAccount();
            if (intent.account === account) save(intent.kind, intent.entity, intent.status);
        },
        dismissError: () => update({ error: null }),
        retry: () => {
            const error = state.error;
            if (error && error.account === account) save(error.kind, error.entity, error.status, error.previousStatus);
        },
    };
};

export const entityPreferenceSessionForStore = store => {
    if (!sessions.has(store)) sessions.set(store, createEntityPreferenceSession(store));
    return sessions.get(store);
};

export const useSession = () => {
    const store = useStore();
    const session = entityPreferenceSessionForStore(store);
    const state = useSyncExternalStore(session.subscribe, session.getSnapshot);
    const user = useSyncExternalStore(store.subscribe, () => store.getState().user);
    return { session, state, user };
};

export const useEntityPreference = (kind, entity) => {
    const { session, state, user } = useSession();
    const { status, mutation, source } = entitySnapshot(kind, entity, state, user);
    return { status, mutation, source, change: nextStatus => session.request(kind, source && kind === 'show' ? { ...entity, source } : entity, nextStatus, status) };
};
